// Fetches the issue-detail bundle: projected issue + resolved links +
// related MRs. One round-trip per page open; the hook clears state on
// `iid` change so navigating between detail pages doesn't briefly show
// the previous issue's data.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  IssueDetailResponseSchema,
  type IssueDetailResponse,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

export interface IssueDetailState {
  readonly data: IssueDetailResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

export const useIssueDetail = (
  projectId: number,
  iid: number | null,
): IssueDetailState => {
  const [data, setData] = useState<IssueDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  const cancelled = useRef(false);

  const refetch = useCallback(() => setBump((n) => n + 1), []);

  useEffect(() => {
    cancelled.current = false;
    setData(null);
    setError(null);
    // New-issue draft (`/issues/new`) mounts the page with no iid — there's
    // nothing to fetch, so settle into a non-loading empty state.
    if (iid === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    httpGet(
      `/api/projects/${projectId}/issues/${iid}`,
      IssueDetailResponseSchema,
    )
      .then((res) => {
        if (cancelled.current) return;
        setData(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled.current) return;
        // A 401 is the route loader's business (it redirects to /sign-in), so
        // this hook paints no error for it — but it MUST still settle. It
        // used to `return` with `loading` still true, which left every
        // consumer that gates on `loading` waiting forever on a request that
        // had already failed: a `loom_session` cookie going stale mid-tab
        // (the long-lived-tab shape ImplementFlow's pre-build MR check sits
        // in) showed a spinner that could never resolve, with no error and no
        // build. Settling with `data: null, error: null` is the same
        // "unknown, proceed" state a 404 body-less answer would leave.
        if (err instanceof UnauthorizedError) {
          setLoading(false);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled.current = true;
    };
  }, [projectId, iid, bump]);

  return { data, loading, error, refetch };
};
